import LocalStorageService from 'web-app/services/local-storage';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {buildUrlWithQueryParameters} from 'web-app/util/url';
import {ACCESS_TOKEN_KEY} from 'web-app/util/access-token-helpers';

import * as Constants from './constants';
import {type ExistingLogin} from './models';

declare const cordova: any;

// IE 11 exposes the WebCrypto library on msCrypto - interface should be the same
// eslint-disable-next-line no-undef
const getRandomValue = () => `${(window.crypto || window.msCrypto).getRandomValues(new Uint32Array(1))[0]}`;

export const buildOpenIdConnectAuthenticationUrl = (
    url: string,
    params: object,
    redirectUrl: string,
    state: string,
) => {
    const paramsWithRedirect = {
        ...params,
        nonce: getRandomValue(),
        state,
        // eslint-disable-next-line camelcase
        redirect_uri: redirectUrl,
    };
    const openConnectIdUrl = buildUrlWithQueryParameters(url, paramsWithRedirect);

    return openConnectIdUrl;
};

const getOrigin = () => {
    return PlatformHelper.isMobileApp()
        ? `https://${Constants.MOBILE_APP_OPENID_REDIRECT_HOSTNAME}${Constants.MOBILE_APP_OPENID_REDIRECT_PATHNAME}`
        : window.location.origin;
};

export const buildOpenIdRedirectUrl = () => {
    return getOrigin();
};

export const buildOpenIdConnectAccountRedirectUrl = () => {
    return getOrigin();
};

export const saveAccessToken = (accessToken: string) => {
    LocalStorageService.saveToLocalStorage(ACCESS_TOKEN_KEY, accessToken);
};

export const postLoginChores = (deviceId: string) => {
    saveDeviceId(deviceId);
    LocalStorageService.clearKey('email');
};

export const saveDeviceId = (deviceId: string) => {
    LocalStorageService.saveToLocalStorage('deviceId', deviceId);
};

export const getDeviceId = () => {
    return LocalStorageService.getFromLocalStorage('deviceId');
};

export const handleOpenIdLogin = (
    openIdUrl: string,
    onRecievedMobileAppCallback: (queryParamsFromRedirect: {code: string}) => void,
) => {
    if (PlatformHelper.isMobileApp()) {
        // Open an in app browser with the link and listen for when it's redirected back to us from the open id provider
        // On Android you'll need to do: openIdUrl.replace('keycloak.famly.local', '10.0.2.2:8032') for the first argument.
        const inAppBrowserInstance = cordova.InAppBrowser.open(
            openIdUrl,
            '_blank',
            'location=no,clearcache=yes,clearsessioncache=yes',
        );
        inAppBrowserInstance.addEventListener('loadstart', event => {
            if (typeof event?.url !== 'string') {
                return;
            }

            const redirectUrl = new URL(event.url);

            if (
                redirectUrl.host === Constants.MOBILE_APP_OPENID_REDIRECT_HOSTNAME &&
                redirectUrl.pathname === Constants.MOBILE_APP_OPENID_REDIRECT_PATHNAME
            ) {
                onRecievedMobileAppCallback(paramsToObject(new URLSearchParams(redirectUrl.search)));
                inAppBrowserInstance.close();
            }
        });
    } else {
        window.location.href = openIdUrl;
    }
};

export const paramsToObject = (entries: any): any => {
    const result = {};
    for (const entry of entries) {
        // each 'entry' is a [key, value] tupple
        const [key, value] = entry;
        result[key] = value;
    }
    return result;
};

export const OPEN_ID_STATE_KEY_KEY = 'openIdState';
const OPEN_ID_STATE_VALUE_KEY = 'openIdStateValue';

export const buildLocalStorageStateValueKey = (stateKey: string) => `${OPEN_ID_STATE_VALUE_KEY}.${stateKey}`;

// Between when we redirect to the provders authentication page and we get redirected back to Famly,
// we need to store some intermediary state.
// The "route" parameter determines where to redirect after user authenticates with the provider.
export const saveOpenIdState = (stateValue: {route: string; [k: string]: string | undefined}): string => {
    const stateKey = getRandomValue();
    LocalStorageService.saveToLocalStorage(OPEN_ID_STATE_KEY_KEY, stateKey);
    LocalStorageService.saveToLocalStorage(buildLocalStorageStateValueKey(stateKey), JSON.stringify(stateValue));
    return stateKey;
};

export const cleanupOpenIdState = () => {
    const stateKey = LocalStorageService.getFromLocalStorage(OPEN_ID_STATE_KEY_KEY);

    if (stateKey) {
        LocalStorageService.clearKey(buildLocalStorageStateValueKey(stateKey));
    }

    LocalStorageService.clearKey(OPEN_ID_STATE_KEY_KEY);
};

// Removes the non-ember query parameters from the url.
// E.g. `/?code=123&state=abc#/account/feed?emberQp=foo` -> /#/account/feed?emberQp=foo
export const cleanupQueryParameters = () => {
    const newUrl = `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState(undefined, window.document.title, newUrl);
};

export const getTLD = (url: string) => {
    if (url.endsWith('.co.uk')) {
        return '.co.uk';
    }

    const splitted = url.split('.');
    return `.${splitted[splitted.length - 1]}`;
};

export const isValidEmail = (email: string | null | undefined) => {
    return typeof email === 'string' && email.match(/\S+@\S+\.\S+/i);
};

const EXISTING_LOGINS_LOCAL_STORAGE_KEY = 'existingLogins';

export const saveExistingLogins = (existingLogins: ExistingLogin[]) => {
    LocalStorageService.saveToLocalStorage(EXISTING_LOGINS_LOCAL_STORAGE_KEY, existingLogins);
};

export const getExistingLogins = () => {
    return LocalStorageService.getFromLocalStorage(EXISTING_LOGINS_LOCAL_STORAGE_KEY) ?? [];
};

export const clearExistingLogins = () => {
    return LocalStorageService.clearKey(EXISTING_LOGINS_LOCAL_STORAGE_KEY);
};
